import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  alpha,
  CircularProgress,
  Stack,
  Chip,
  Grid,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";

/**
 * Empty state dedicado para Tipos de atendimento / solicitações.
 * Layout semelhante ao usado em Etapas de Consciência, com foco no CTA de IA.
 */
const EmptyStateRequestTypes = ({
  description,
  onPrimaryClick,
  onSecondaryClick,
  primaryLoading = false,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const primary = theme.palette.primary.main;
  const primaryLight = alpha(primary, isDark ? 0.2 : 0.12);
  const primaryBg = alpha(primary, isDark ? 0.08 : 0.06);

  const chips = [
    { icon: <SupportAgentOutlinedIcon sx={{ fontSize: 20 }} />, label: "Atendimento organizado" },
    { icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 20 }} />, label: "Vendas classificadas" },
    { icon: <CategoryOutlinedIcon sx={{ fontSize: 20 }} />, label: "Métricas por tipo" },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: 960,
        mx: "auto",
        overflow: "hidden",
        borderRadius: 3,
        border: `1px solid ${alpha(primary, isDark ? 0.25 : 0.18)}`,
        background: isDark
          ? `linear-gradient(160deg, ${alpha(
              theme.palette.background.paper,
              0.98
            )} 0%, ${alpha(primary, 0.06)} 50%, ${alpha(
              theme.palette.background.paper,
              0.98
            )} 100%)`
          : `linear-gradient(160deg, ${theme.palette.background.paper} 0%, ${primaryBg} 40%, ${
              theme.palette.background.paper
            } 100%)`,
        boxShadow: isDark
          ? `0 12px 40px ${alpha(theme.palette.common.black, 0.35)}, inset 0 1px 0 ${alpha(
              theme.palette.common.white,
              0.05
            )}`
          : `0 12px 40px ${alpha(primary, 0.1)}, inset 0 1px 0 ${alpha(
              theme.palette.common.white,
              0.8
            )}`,
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Grid container spacing={4} alignItems="stretch">
          {/* Coluna esquerda: hero + texto + chips */}
          <Grid item xs={12} md={7}>
            <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 64,
                    height: 64,
                    flexShrink: 0,
                    borderRadius: "50%",
                    bgcolor: primaryLight,
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      inset: -4,
                      borderRadius: "50%",
                      border: `2px solid ${alpha(primary, 0.2)}`,
                    },
                  }}
                >
                  <AssignmentOutlinedIcon sx={{ fontSize: 36 }} color="primary" />
                </Box>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 1.1 }}
                  >
                    Tipos de atendimento
                  </Typography>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{ fontWeight: 700, color: theme.palette.text.primary, mt: 0.25 }}
                  >
                    Nenhum tipo cadastrado
                  </Typography>
                </Box>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.75, mb: 2, flex: 1 }}
              >
                {description}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {chips.map((item, idx) => (
                  <Chip
                    key={idx}
                    icon={item.icon}
                    label={item.label}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: alpha(primary, 0.35),
                      color: theme.palette.text.secondary,
                      "& .MuiChip-icon": { color: "primary.main" },
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Grid>

          {/* Coluna direita: dica + CTAs */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: alpha(primary, isDark ? 0.08 : 0.06),
                  borderLeft: `4px solid ${primary}`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  💡 Use sugestões de IA para criar rapidamente a estrutura de tipos e subtipos ou
                  adicione-os manualmente conforme o processo da sua operação.
                </Typography>
              </Box>
              <Stack
                direction={{ xs: "column", sm: "column" }}
                spacing={2}
                sx={{ mt: { md: 0 } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={
                    primaryLoading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      <AutoAwesomeIcon />
                    )
                  }
                  disabled={primaryLoading}
                  onClick={onPrimaryClick}
                  sx={{
                    py: 1.5,
                    fontWeight: 600,
                    boxShadow: 2,
                    borderRadius: 2,
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  {primaryLoading ? "Gerando sugestões..." : "Criar tipos com sugestões de IA"}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  startIcon={<AssignmentOutlinedIcon />}
                  onClick={onSecondaryClick}
                  sx={{ py: 1.5, fontWeight: 500, borderRadius: 2 }}
                >
                  Adicionar tipo manualmente
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default EmptyStateRequestTypes;

